"use strict";
/**
 * 预览令牌的**传输**规则（P1-17 内联预览）。
 *
 * 令牌本身（签名、校验）在 `@icetus/preview-token`，那个包依赖 `node:crypto`，
 * 客户端组件碰不得。这里只放纯字符串处理，因此服务端渲染、控制台、测试都能用。
 *
 * ## 为什么需要第二种传输方式
 *
 * 原来的设计只有 cookie：点「预览」→ `/api/preview?token=…` 验签后写
 * `icetus_preview`（httpOnly + SameSite=Lax）→ 307 回干净地址。这在**新标签页**
 * 里完全正确，但它有一个硬约束：SameSite=Lax 的 cookie 只在**顶级导航**里发送。
 *
 * 控制台的域名是 `console.icetus.net`，客户站点是 `schoolmemo.cn`，两者跨站。
 * 把站点嵌进控制台的 iframe 时，iframe 的每一次请求都是跨站子请求 ——
 * Lax cookie 按规范**根本不会被带上**（这不是浏览器的隐私开关，是 SameSite 的语义），
 * Safari 对第三方 cookie 更是全阻。于是内联预览表现为"一直在看线上版本"，
 * 而本地开发时控制台与站点都在 `localhost`（同站），cookie 一切正常 ——
 * 这个组合正好是"本地全绿、线上失效"。
 *
 * 所以 iframe 走查询串：`?__icetus_preview=<token>`。代价是令牌进了地址栏，
 * 且**站内链接必须自己带上它**（否则点一下导航就掉出预览，客户以为在看草稿，
 * 实际看的是已发布内容）。为此服务端在预览态会把令牌拼进它生成的每一个站内链接。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PREVIEW_QUERY_PARAM = void 0;
exports.withPreviewToken = withPreviewToken;
exports.readPreviewToken = readPreviewToken;
exports.stripPreviewToken = stripPreviewToken;
exports.tagPreviewLinks = tagPreviewLinks;
exports.PREVIEW_QUERY_PARAM = '__icetus_preview';
const MARK = `${exports.PREVIEW_QUERY_PARAM}=`;
/**
 * 给一个站内链接带上预览令牌。
 *
 * 只动站内绝对路径（以单个 `/` 开头）。外链、`mailto:`、`tel:`、纯锚点
 * （`#faq`）一律原样返回：给 `https://…` 拼一个我们自己的查询参数，等于把
 * 令牌送给第三方站点，对方看 Referer 就能读到。
 */
function withPreviewToken(href, token) {
    if (!token)
        return href;
    if (!href.startsWith('/'))
        return href;
    // `//evil.com` 是协议相对 URL，浏览器当外站处理
    if (href.startsWith('//'))
        return href;
    // 已经有了就别再拼一次：重复的查询参数会让链接越点越长，且服务端只认第一个
    if (href.includes(MARK))
        return href;
    const hashAt = href.indexOf('#');
    const path = hashAt === -1 ? href : href.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : href.slice(hashAt);
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}${MARK}${encodeURIComponent(token)}${hash}`;
}
/** 从查询串里读出令牌。只取第一个，重复参数不做合并 */
function readPreviewToken(search) {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    if (!raw)
        return null;
    for (const pair of raw.split('&')) {
        const eq = pair.indexOf('=');
        if (eq === -1)
            continue;
        if (pair.slice(0, eq) !== exports.PREVIEW_QUERY_PARAM)
            continue;
        const value = pair.slice(eq + 1);
        if (!value)
            return null;
        try {
            return decodeURIComponent(value);
        }
        catch {
            // 非法的百分号编码 —— 当成没有，不要抛：调用方在渲染链上，抛出去就是整页 500
            return null;
        }
    }
    return null;
}
/** 去掉预览参数，得到"线上版"的地址（预览条上的「看线上版」用） */
function stripPreviewToken(href) {
    const hashAt = href.indexOf('#');
    const path = hashAt === -1 ? href : href.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : href.slice(hashAt);
    const qAt = path.indexOf('?');
    if (qAt === -1)
        return href;
    const keep = path
        .slice(qAt + 1)
        .split('&')
        .filter((pair) => pair && !pair.startsWith(MARK));
    return `${path.slice(0, qAt)}${keep.length > 0 ? `?${keep.join('&')}` : ''}${hash}`;
}
const HREF_ATTR = /(\shref=)(["'])(\/[^"']*)\2/g;
/**
 * 把一段 HTML 里所有站内链接带上令牌（富文本区块用）。
 *
 * 富文本正文是客户自己写的 HTML，链接散落在正文里，没有"生成链接"的代码点可以挂，
 * 只能在渲染时扫一遍。安全性上不担心：这段 HTML 进 DOM 之前已经过了
 * `sanitizeRichText` 重建式白名单，`href` 的取值受控、引号统一 ——
 * 在**清洗之后**做这个替换，而不是之前。
 */
function tagPreviewLinks(html, token) {
    if (!token)
        return html;
    return html.replace(HREF_ATTR, (_whole, attr, quote, href) => {
        return `${attr}${quote}${withPreviewToken(href, token)}${quote}`;
    });
}
//# sourceMappingURL=preview.js.map